import { Event } from './event';
import { Events } from './event/events';
import {
  EventManagerInterface,
  Handler,
  instanceOfStoppableEventInterface,
  ObjectHandler,
} from '../../interfaces/components/event-manager/event-manager.interface';
import { EventSubscriberInterface } from '../../interfaces/components/event-manager/subscriber/event-subscriber.interface';
import { Injectable } from '../dependency-injection/decorators';
import { Logger } from '../logger/logger';

@Injectable()
export class EventManager implements EventManagerInterface {
  private readonly listeners: Map<Events, Array<Handler<Event>>>;

  constructor (private readonly logger: Logger) {
    this.listeners = new Map();
  }

  public addListener (type: Events, handler: Handler<Event> | [ object, string ] | ObjectHandler): void {
    const callee = this.resolveHandler(type, handler);

    if (!callee) {
      return;
    }

    const eventListeners = this.listeners.get(type) ?? [];

    eventListeners.push(callee);
    this.listeners.set(type, eventListeners);
  }

  public removeListener (type: Events, handler: Handler<Event> | [ object, string ] | ObjectHandler): void {
    if (!this.listeners.has(type)) {
      return;
    }

    const callee = this.resolveHandler(type, handler);

    if (!callee) {
      return;
    }

    const listeners = this.listeners.get(type) ?? [];

    for (let i = 0; i < listeners.length; i++) {
      const listener = listeners[ i ];

      if ('__ee__' in listener && '__ee__' in callee && listener[ '__ee__' ] === callee[ '__ee__' ]) {
        listeners.splice(i, 1);
      }
    }

    this.listeners.set(type, listeners);

    if (listeners.length === 0) {
      this.listeners.delete(type);
    }
  }

  public addSubscriber (subscriber: EventSubscriberInterface): void {
    for (const [ eventName, methodName ] of Object.entries(subscriber.getSubscribedEvents())) {
      this.addListener(eventName as Events, [ subscriber, methodName ]);
    }
  }

  public removeSubscriber (subscriber: EventSubscriberInterface): void {
    for (const [ eventName, methodName ] of Object.entries(subscriber.getSubscribedEvents())) {
      this.removeListener(eventName as Events, [ subscriber, methodName ]);
    }
  }

  public async dispatch<T extends Event> (type: Events, event?: T): Promise<T> {
    if (!event) {
      event = Event.getEmptyInstance() as T;
    }

    this.logger.debug(`Dispatching event ${type} for event payload ${event?.constructor.name}`, { event });

    for (const listener of this.listeners.get(type) ?? []) {
      if (instanceOfStoppableEventInterface(event) && event.isPropagationStopped()) {
        break;
      }

      await listener(event, type);
    }

    this.logger.debug(`Dispatched event ${type} for event payload ${event?.constructor.name}`, { event });

    return event;
  }

  public hasListeners (): boolean {
    return this.listeners.size > 0;
  }

  public getListeners (type: Events): Handler<Event>[] {
    return this.listeners.get(type) ?? [];
  }

  private resolveHandler (
    type: Events,
    handler: Handler<Event> | [ object, string ] | ObjectHandler,
  ): Handler<Event> | null {
    let callee: Handler<Event> | null = null;

    let thisArg: object | undefined = undefined;
    let resolvedHandler: Handler<Event> | undefined = undefined;

    if (typeof handler === 'function') {
      thisArg = {};
      resolvedHandler = handler;
    } else if (typeof handler === 'object' && type in handler && typeof handler[ type as never ] === 'function') {
      thisArg = handler;
      resolvedHandler = handler[ type as never ] as Handler<Event>;
    } else if (Array.isArray(handler) &&
      typeof handler[ 0 ] === 'object' &&
      handler[ 1 ] in handler[ 0 ] &&
      typeof handler[ 0 ][ handler[ 1 ] as never ] === 'function'
    ) {
      thisArg = handler[ 0 ];
      resolvedHandler = handler[ 0 ][ handler[ 1 ] as never ] as Handler<Event>;
    }

    if (typeof resolvedHandler === 'undefined' || typeof thisArg === 'undefined') {
      return callee;
    }

    callee = async (event, eventName): Promise<void> =>
      resolvedHandler?.call(thisArg, event, eventName);

    Object.defineProperty(callee, '__ee__', {
      value: resolvedHandler,
    });

    return callee;
  }
}
